'use client';

import { create } from 'zustand';
import type { AuthClientResponse } from '@sola/schema/dotcom/auth.ts';
import { AuthScenario } from '@sola/schema/dotcom/auth.ts';
import { AccountCreatePayload } from '@sola/schema/dotcom/auth/account.ts';
import { AuthRedirectPage, AuthRedirectPagePaths, AuthSearchParam } from '@sola/schema/dotcom/auth/routes.ts';
import { getAuthClientCookie, updateAuthClientCookie } from '@sola/shared-utils/auth/frontendCookie.ts';
import { hasAuth } from '@sola/shared-utils/frontend-sessions.ts';
import { getEnvironmentRoot } from '@sola/shared-utils/misc.ts';
import { type ZustandSelectedSlices, type ZustandSliceKey, useZustandSlices } from '@sola/shared-utils/react.tsx';
import type { AuthAppConfig } from './apps.ts';
import { APPROVED_APPS } from './apps.ts';
import { accountCreate, authenticateUser, confirmAccountEmail, forgotPassword, forgotPasswordValidate, logoutUser, recoverUsername, resendConfirmationEmail, signup, signupValidationResend, userCreate, userCreateFromAccount, verifyUser } from './requests.ts';
export enum AuthStatusEnum {
  Authenticated = 'AUTHENTICATED',
  LoggedOut = 'LOGGED_OUT',
  AccountCreated = 'ACCOUNT_CREATED',
  AccountCreatedAndAuthenticated = 'ACCOUNT_CREATED_AND_AUTHENTICATED',
  AccountCreatedConfirmed = 'ACCOUNT_CREATED_CONFIRMED',
  InProgress = 'IN_PROGRESS',
  Success = 'SUCCESS',
}
interface AuthError {
  type: AuthScenario;
  message: string;
}
interface AuthActions {
  checkIfUserIsAuthenticated: () => Promise<void>;
  logIn: (username: string, password: string, includeSupporter?: boolean) => Promise<AuthClientResponse | null>;
  logOut: () => Promise<void>;
  userCreateFromAccount: (globalId: string, postalCode: string, username: string, password: string, email: string) => Promise<void>;
  userCreate: (username: string, password: string, uniqueId: string) => Promise<void>;
  accountCreate: (args: AccountCreatePayload) => Promise<void>;
  resendConfirmationEmail: (args: {
    authUsername?: string;
    email?: string;
  }) => Promise<void>;
  signupValidationResend: (uniqueId: string) => Promise<void>;
  forgotPassword: (email: string, username?: string, sponsorDonorNumber?: string) => Promise<void>;
  forgotPasswordValidate: (authUsername: string, validationCode: string, newPassword: string) => Promise<void>;
  confirmEmail: (authUsername: string, validationCode: string) => Promise<void>;
  signup: (email: string) => Promise<void>;
  recoverUsername: (email: string, sponsorDonorNumber?: string) => Promise<void>;
  getReturnLink: () => string;
  getError: () => AuthError | null;
  clearError: () => void;
  getCurrentStatus: () => AuthStatusEnum | null;
}
interface FormState {
  email?: string;
  uniqueId?: string; // Not Sure we are going to store this.
}
export interface AuthState {
  actions: AuthActions;
  appConfig: AuthAppConfig | null;
  authUser?: AuthClientResponse | null;
  currentStatus: AuthStatusEnum;
  error: AuthError | null;
  formState?: FormState;
  isReady: boolean;
  isLoggedIn: () => boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  returnUrl: URL | null;
  createPath: string;
}

/**
 * Internal hook to create the zustand store.
 */
export const useAuthStore = create<AuthState>()((set, get) => {
  return {
    appConfig: null,
    authUser: null,
    currentStatus: AuthStatusEnum.InProgress,
    error: null,
    returnUrl: null,
    createPath: AuthRedirectPagePaths[AuthRedirectPage.Create],
    isReady: false,
    isLoggedIn: () => {
      const {
        currentStatus
      } = get();
      return currentStatus === AuthStatusEnum.Authenticated || currentStatus === AuthStatusEnum.Success;
    },
    isLoggingIn: false,
    isLoggingOut: false,
    actions: {
      // TODO: Even if the user is not authenticated, there might be an authuser object present because logout would never have been called. Should we clear out the auth user object everytime we run init?
      checkIfUserIsAuthenticated: async () => {
        try {
          // Set status to InProgress
          set({
            currentStatus: AuthStatusEnum.InProgress
          });

          // Run /verify api call.
          const verifyResponse = await verifyUser();
          if (verifyResponse && verifyResponse.success) {
            // Set status to LOGGED_IN
            // Set authUser to auth object.
            set({
              authUser: verifyResponse,
              currentStatus: AuthStatusEnum.Authenticated
            });
          } else {
            // If error or not authenticated set status to LOGGED_OUT
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null
            });
          }
        } catch {
          // if error, set status to logged_out
          // set errors here or return error?
          const authState = get();
          set({
            error: {
              type: AuthScenario.NOT_AUTHORIZED,
              message: authState.error?.message || ''
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
        set({
          isReady: true
        });
      },
      logIn: async (username, password, includeSupporter) => {
        // if username and password not provided return error.
        if (!username || !password) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Missing username or password'
            }
          });
          return null;
        }
        // set status to logging_in
        set({
          error: null,
          isLoggingIn: true,
          currentStatus: AuthStatusEnum.InProgress
        });
        try {
          const response = await authenticateUser({
            username,
            password,
            includeSupporter
          });
          if (response && response.success) {
            set({
              authUser: response,
              currentStatus: AuthStatusEnum.Authenticated,
              isLoggingIn: false
            });
            return response;
          }
          if (response && !response.success) {
            if (response.type === AuthScenario.REQUIRED_EMAIL_VERIFICATION) {
              set({
                // TODO: Isn't this authUser just reset to null below?
                authUser: {
                  success: true,
                  authUsername: username,
                  authUserGlobalId: '',
                  authUserInternalId: '',
                  authUserPrimaryContactId: ''
                }
              });
            }
            set({
              error: {
                type: response.type,
                message: response.message
              },
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null
            });
          }
        } catch (error: unknown) {
          console.error('Unexpected error:', error);
          set({
            error: {
              message: 'Unexpected error',
              type: AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
        return null;
      },
      logOut: async () => {
        try {
          // set status to logging_in
          set({
            currentStatus: AuthStatusEnum.InProgress,
            authUser: null,
            error: null,
            isLoggingOut: true
          });

          // then, run /authenticate
          const results = await logoutUser();

          // if succesful, set status to authenticated
          if (results) {
            // set authuser to auth object
            set({
              authUser: null,
              currentStatus: AuthStatusEnum.LoggedOut,
              isLoggingOut: false
            });
          }
        } catch {
          // if error, set status to logged_out
          // set errors here or return error?
          // todo: add-error util
          const authState = get();
          // const newError: AuthError = {
          //   // type: CheckoutErrorType.Bad,
          //   message: error.message,
          // };
          set({
            error: authState.error,
            // errors: [...errors, newError],
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null
          });
        }
      },
      userCreateFromAccount: async (globalId: string, postalCode: string, username: string, password: string, email: string) => {
        // If email is provided, there is no need for global id and postal code
        if (!username || !password || globalId && !postalCode || !postalCode && !globalId && !email) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress,
          formState: {
            email
          },
          authUser: {
            success: true,
            authUsername: globalId || username,
            authUserGlobalId: globalId || '',
            authUserInternalId: '',
            authUserPrimaryContactId: ''
          }
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await userCreateFromAccount({
            globalId,
            postalCode,
            username,
            password,
            email,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return;
          }
          if (response.success) {
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED_AND_AUTHENTICATED && response.sessionJwt) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedAndAuthenticated
              });
              return;
            }
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedConfirmed
              });
              return;
            }
            set({
              authUser: {
                success: true,
                authUsername: globalId || username,
                authUserGlobalId: globalId || '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              },
              formState: {
                email
              },
              currentStatus: AuthStatusEnum.AccountCreated
            });
          } else {
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: response?.message || 'Unknown error',
                type: response?.type || AuthScenario.UNKNOWN_ERROR
              }
            });
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      userCreate: async (username, password, uniqueId) => {
        if (!username || !password || !uniqueId) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress,
          formState: {
            uniqueId
          }
        });
        try {
          const response = await userCreate({
            username,
            password,
            uniqueId
          });
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return;
          }
          if (response.success) {
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED_AND_AUTHENTICATED && response.sessionJwt) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedAndAuthenticated
              });
              return;
            }
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedConfirmed
              });
              return;
            }
            set({
              currentStatus: AuthStatusEnum.AccountCreated
            });
          } else {
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: response?.message || 'Unknown error',
                type: response?.type || AuthScenario.UNKNOWN_ERROR
              }
            });
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      accountCreate: async (args: AccountCreatePayload) => {
        const parsed = AccountCreatePayload.safeParse(args);
        if (!parsed.success) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress,
          formState: {
            uniqueId: args.uniqueId
          }
        });
        try {
          const response = await accountCreate(args as AccountCreatePayload);
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return;
          }
          if (response.success) {
            // TODO: We will not have a globalId or username from create account,
            // should authUser still be set to null.
            set({
              formState: {
                uniqueId: response.uniqueId
              },
              currentStatus: AuthStatusEnum.AccountCreated
            });
          } else {
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: response?.message || 'Unknown error',
                type: response?.type || AuthScenario.UNKNOWN_ERROR
              }
            });
          }
        } catch (error) {
          console.log('error', error);
          console.error('Unexpected error:', error);
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      signup: async email => {
        if (!email) {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.UNKNOWN_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        const {
          appConfig,
          returnUrl
        } = get();
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await signup(email, appConfig?.key, returnUrl?.toString());
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            set({
              error: null,
              currentStatus: AuthStatusEnum.Success,
              // TODO: I'm thinking this isn't needed. If we are abandoning the signup flow with the check email
              // then the user always clicks the link and we validate their link we are going to redirect them to
              // the next steps will not be using this same auth state.
              formState: {
                email,
                uniqueId: response?.uniqueId
              }
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      confirmEmail: async (authUsername, confirmationCode: string) => {
        if (!authUsername) {
          // TODO: if we don't have the username either in the authUsernamParam or the state should we give them a better message:
          // to re-click the link and if you continue to have issues call this number.. our number.
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.UNKNOWN_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await confirmAccountEmail({
            confirmationCode,
            authUsername
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            set({
              error: null,
              currentStatus: AuthStatusEnum.AccountCreatedConfirmed
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      resendConfirmationEmail: async (args: {
        authUsername?: string;
        email?: string;
      }) => {
        const {
          authUsername,
          email
        } = args;
        const {
          authUser
        } = get();
        const currentUsername = authUsername || authUser?.authUsername || undefined;
        if (!currentUsername && !email) {
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            error: {
              message: 'Missing username',
              type: AuthScenario.MISSING_FIELDS
            }
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await resendConfirmationEmail({
            authUsername: currentUsername,
            email,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            const responseUsername = currentUsername || response?.authUsername;
            if (!responseUsername) {
              set({
                error: {
                  type: AuthScenario.INTERNAL_ERROR,
                  message: 'Unknown error'
                }
              });
              return;
            }
            set({
              authUser: {
                success: true,
                authUsername: responseUsername,
                authUserGlobalId: '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              },
              formState: {
                email
              },
              currentStatus: AuthStatusEnum.Success
            });
          }
        } catch {
          // TODO: These caught errors need cleaning up and reasoning through...
          // Is the error coming from the catch or the state?
          const {
            error: stateError
          } = get();
          const newError: AuthError = {
            message: stateError?.message || 'Unknown error',
            type: stateError?.type || AuthScenario.INTERNAL_ERROR
          };
          set({
            error: newError,
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      signupValidationResend: async uniqueId => {
        if (!uniqueId) {
          // TODO: resend them to /signup?
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            error: {
              message: 'Missing params',
              type: AuthScenario.MISSING_FIELDS
            }
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await signupValidationResend({
            uniqueId
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            set({
              formState: {
                // email, // TODO: pass email in for next step to check email?
                uniqueId
              },
              currentStatus: AuthStatusEnum.Success
            });
          }
        } catch {
          // TODO: These caught errors need cleaning up and reasoning through...
          // Is the error coming from the catch or the state?
          const {
            error: stateError
          } = get();
          const newError: AuthError = {
            message: stateError?.message || 'Unknown error',
            type: stateError?.type || AuthScenario.INTERNAL_ERROR
          };
          set({
            error: newError,
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      forgotPassword: async (email: string, username?: string, globalId?: string) => {
        if (!email) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Missing email'
            }
          });
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          authUser: null,
          error: null
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await forgotPassword({
            email,
            username,
            globalId,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (response && !response.success) {
            set({
              formState: {
                email
              },
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else if (response) {
            set({
              authUser: {
                success: true,
                authUsername: response?.authUsername,
                authUserGlobalId: '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              }
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      forgotPasswordValidate: async (authUsername: string, validationCode: string, password: string) => {
        try {
          if (!authUsername || !validationCode || !password) {
            set({
              error: {
                type: AuthScenario.MISSING_FIELDS,
                message: 'Missing email'
              }
            });
          }
          set({
            currentStatus: AuthStatusEnum.InProgress,
            error: null
          });
          const response = await forgotPasswordValidate({
            authUsername,
            validationCode,
            password
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      recoverUsername: async (email: string, sponsorDonorNumber?: string) => {
        if (!email) {
          set({
            error: {
              message: 'Missing email',
              type: AuthScenario.MISSING_FIELDS
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          authUser: null,
          error: null
        });
        try {
          const response = await recoverUsername({
            email,
            sponsorDonorNumber
          });
          if (response && !response.success) {
            set({
              formState: {
                email
              },
              error: {
                type: response.type,
                message: response.message
              }
            });
          }
        } catch {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      getReturnLink: () => {
        const {
          authUser,
          appConfig,
          returnUrl
        } = get();
        const appender = returnUrl?.href.includes('?') ? '&' : '?';
        const addJwt = appConfig && authUser?.sessionJwt ? `${appender}${AuthSearchParam.Jwt}=${authUser?.sessionJwt}` : '';
        return `${returnUrl?.href}${addJwt}`;
      },
      getError: () => {
        const {
          error
        } = get();
        return error;
      },
      clearError: () => {
        set({
          error: null
        });
      },
      getCurrentStatus: () => {
        const {
          currentStatus
        } = get();
        return currentStatus;
      }
    }
  };
});

/*
 * Hook that allows consumers to easily implement slices on auth store.
 */
export const useAuth = <TKeys extends readonly ZustandSliceKey<AuthState>[],>(...props: TKeys): ZustandSelectedSlices<AuthState, TKeys> => {
  return useZustandSlices<AuthState, TKeys>(useAuthStore, ...props);
};

/**
 * Function to initialize auth store.
 */
export const initAuth = async (isAuthCompassion: boolean) => {
  if (isAuthCompassion) {
    const appCookies = getAuthClientCookie();
    const url = new URL(globalThis.location.href);
    if (!url.search && appCookies) {
      url.search = appCookies;
    }
    // Not using useSearchParams to prevent needing Suspense boundaries
    const app = url.searchParams.get(AuthSearchParam.App);
    // When coming to auth, the public contract is "redirect" but internally we store and refer to it as "returnUrl"...
    const returnQuery = url.searchParams.get(AuthSearchParam.Redirect);
    const appConfig = app && Object.keys(APPROVED_APPS).includes(app) ? APPROVED_APPS[app] : null;
    const mainRedirectUrl = process.env.NEXT_PUBLIC_FF_DEFAULT_TO_MY === 'true' ? process.env.NEXT_PUBLIC_BASE_URL_MY || 'https://my.compassion.com' : process.env.NEXT_PUBLIC_BASE_URL_DOTCOM || 'https://www.compassion.com';
    const envDomain = getEnvironmentRoot(mainRedirectUrl);
    const domain = appConfig?.domain || envDomain;
    const returnUrl = returnQuery && URL.canParse(returnQuery) ? new URL(returnQuery) : null;
    const verifiedReturnUrl = returnUrl?.hostname === domain || returnUrl?.hostname.endsWith(`.${domain}`) ? returnUrl : new URL(mainRedirectUrl);
    useAuthStore.setState({
      returnUrl: verifiedReturnUrl,
      ...(appConfig?.useSignup && {
        createPath: AuthRedirectPagePaths[AuthRedirectPage.Signup]
      })
    });
    if (url.search && (app || returnUrl)) {
      updateAuthClientCookie(url.search);
    }
    if (appConfig) {
      useAuthStore.setState({
        appConfig
      });
    }
  }

  // Procceed if have no auth cookie.
  if (!hasAuth()) {
    useAuthStore.setState({
      currentStatus: AuthStatusEnum.LoggedOut,
      isReady: true
    });
    return;
  }
  const {
    actions
  } = useAuthStore.getState();
  await actions.checkIfUserIsAuthenticated();
};